
import {
  computed, defineComponent, PropType, reactive, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import type { FlowSettingsAutomation, FlowSettingsPeriod, FlowScheduleConfig } from '@/types/flow-settings';
import { FLOW_SCHEDULE_VERSION } from '@/types/flow-settings';
import {
  parseScheduleConfig,
  serializeScheduleConfig,
  migrateLegacyDateRanges,
  buildLegacyDateRangesMirror,
} from '@/utils/schedule';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import ScheduleBuilder from '@/components/flows/settings/schedule/ScheduleBuilder.vue';
import {
  ButtonGroup,
  PeriodSelector,
} from '@/components/flows/operators/shared';

export default defineComponent({
  name: 'FlowSettingsAutomation',

  components: {
    InputText,
    FieldErrors,
    PeriodSelector,
    ButtonGroup,
    ScheduleBuilder,
  },

  props: {
    settings: {
      type: Object as PropType<FlowSettingsAutomation>,
      required: true,
    },

    validationErrors: {
      type: Object as PropType<Record<string, string[]>>,
      default: () => ({}),
    },
  },

  emits: ['update'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Local reactive copy of settings
    const localSettings = reactive<FlowSettingsAutomation>({ ...props.settings });

    // Watch for external settings changes
    watch(() => props.settings, (newSettings) => {
      Object.assign(localSettings, newSettings);
    }, { deep: true });

    // Options
    const typeWorkflowOptions = computed(() => [
      { value: 'marketing', label: t('automatedScenarios.globalSettings.type_workflow.options.marketing') },
      { value: 'transactional', label: t('automatedScenarios.globalSettings.type_workflow.options.transactional') },
    ]);

    const yesNoOptions = computed(() => [
      { value: 1, label: t('yes') },
      { value: 0, label: t('no') },
    ]);

    const typeUniqueOptions = computed(() => [
      { value: 1, label: t('automatedScenarios.globalSettings.type_unique.options.visitor') },
      { value: 0, label: t('automatedScenarios.globalSettings.type_unique.options.session') },
    ]);

    const periodUnitOptions = computed(() => [
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
      { value: 'MONTH', label: t('automatedScenarios.fields.durations.month(s)') },
    ]);

    // Emit helpers
    const emitUpdate = (field: keyof FlowSettingsAutomation, value: any) => {
      (localSettings as any)[field] = value;
      emit('update', { [field]: value });
    };

    const setTypeWorkflow = (value: string) => {
      emitUpdate('type_workflow', value as 'marketing' | 'transactional');
    };

    const setUnique = (value: number) => {
      const val = value as 0 | 1;
      localSettings.unique = val;
      // Reset dependent fields
      if (val === 0) {
        localSettings.type_unique = null;
        localSettings.type_retrigger = null;
      } else {
        // When enabling unique, reset to default values if null
        if (localSettings.type_unique === null) {
          localSettings.type_unique = 0;
        }
        if (localSettings.type_retrigger === null) {
          localSettings.type_retrigger = 0;
        }
      }
      emit('update', {
        unique: val,
        type_unique: val === 0 ? null : localSettings.type_unique,
        type_retrigger: val === 0 ? null : localSettings.type_retrigger,
      });
    };

    const setTypeUnique = (value: number) => {
      const val = value as 0 | 1;
      localSettings.type_unique = val;
      // Reset dependent field
      if (val === 0) {
        localSettings.type_retrigger = null;
      } else if (localSettings.type_retrigger === null) {
        // When enabling visitor unique, reset type_retrigger to default if null
        localSettings.type_retrigger = 0;
      }
      emit('update', {
        type_unique: val,
        type_retrigger: val === 0 ? null : localSettings.type_retrigger,
      });
    };

    const setTypeRetrigger = (value: number) => {
      emitUpdate('type_retrigger', value as 0 | 1);
    };

    const setTrackSales = (value: number) => {
      emitUpdate('track_sales', value as 0 | 1);
    };

    const updatePeriod = (period: FlowSettingsPeriod) => {
      localSettings.time_unit_select = period;
      emit('update', { time_unit_select: period });
    };

    /* ──────────────────────────────────────────────────────────────────────
     * Advanced scheduling (v2)
     *
     * Retro-compat: on load we show the v2 `schedule_config` if present, else
     * we migrate the legacy `date_ranges` so an existing scenario is faithfully
     * restored. On change we persist the v2 config AND a best-effort legacy
     * `date_ranges` mirror (so a back that only understands v1 keeps enforcing
     * the date part). An old scenario left untouched keeps its original
     * `date_ranges` because we only write when the user actually edits.
     * ────────────────────────────────────────────────────────────────────── */
    const buildInitialScheduleConfig = (): FlowScheduleConfig => {
      const parsed = parseScheduleConfig(localSettings.schedule_config);
      if (parsed) return parsed;
      return migrateLegacyDateRanges(localSettings.date_ranges);
    };

    const scheduleConfig = ref<FlowScheduleConfig>(buildInitialScheduleConfig());

    // A rule is worth persisting only if it constrains at least one dimension;
    // an empty (just-added) rule would otherwise match everything and silently
    // disable the schedule. Filter those out of what we serialise / mirror.
    const isRuleMeaningful = (rule: FlowScheduleConfig['rules'][number]): boolean => !!(
      rule.date || (rule.weekdays && rule.weekdays.length) || rule.time
    );

    const cleanScheduleForSave = (config: FlowScheduleConfig): FlowScheduleConfig => ({
      version: config.version || FLOW_SCHEDULE_VERSION,
      enabled: localSettings.program_date_ranges === 1,
      timezone_mode: config.timezone_mode || 'shop',
      rules: config.rules.filter(isRuleMeaningful),
    });

    const syncScheduleToSettings = () => {
      const clean = cleanScheduleForSave(scheduleConfig.value);
      const scheduleJson = serializeScheduleConfig(clean);
      const mirrorJson = JSON.stringify(buildLegacyDateRangesMirror(clean));
      localSettings.schedule_config = scheduleJson;
      localSettings.date_ranges = mirrorJson;
      emit('update', {
        schedule_config: scheduleJson,
        date_ranges: mirrorJson,
        program_date_ranges: 1,
      });
    };

    // React to edits coming from the builder (only while scheduling is on).
    watch(scheduleConfig, () => {
      if (localSettings.program_date_ranges === 1) syncScheduleToSettings();
    }, { deep: true });

    const setProgramDateRanges = (value: number) => {
      const val = value as 0 | 1;
      localSettings.program_date_ranges = val;
      if (val === 0) {
        // Turning scheduling off clears both the v2 config and the legacy mirror.
        localSettings.date_ranges = '[]';
        localSettings.schedule_config = '';
        emit('update', { program_date_ranges: 0, date_ranges: '[]', schedule_config: '' });
      } else {
        // Turning it on persists the current (possibly migrated) schedule.
        syncScheduleToSettings();
      }
    };

    return {
      t,
      localSettings,
      typeWorkflowOptions,
      yesNoOptions,
      typeUniqueOptions,
      periodUnitOptions,
      emitUpdate,
      setTypeWorkflow,
      setUnique,
      setTypeUnique,
      setTypeRetrigger,
      setTrackSales,
      setProgramDateRanges,
      updatePeriod,
      scheduleConfig,
    };
  },
});
